<template>
    <VJoyIconButton
        v-if="!props.disabled"
        :data-testid="`share-profile-social-network--${socialNetwork}`"
        :icon="socialNetworkIcon"
        variant="secondary"
        :data-test-link="socialNetworkLink"
        @click="handleClickOnShare"
    />
    <VJoyIconButton
        v-else
        :data-testid="`share-profile-social-network--${socialNetwork}--disabled`"
        disabled
        :icon="socialNetworkIcon"
        variant="secondary"
    />
</template>
<script setup lang="ts">
    import {computed} from 'vue';
    import {safelyNavigateTo} from '@malt/utils';
    import {VJoyIconButton} from '@maltjoy/core-vue';
    import {useProfilePageViewMode} from '~/composables/useProfilePageViewMode';
    import {useProfileTracking} from '~/composables/useProfileTracking';

    type ShareProfileSocialNetworkProps = {
        profileLink: string;
        profileFirstname: string;
        profileLastname: string;
        profileHeadline: string;
        profileAccountId: string;
        socialNetwork: 'linkedin' | 'facebook';
        disabled?: boolean;
    };
    const props = defineProps<ShareProfileSocialNetworkProps>();

    // Composables
    const {trackProfileSocialShareStarted} = useProfileTracking();
    const {isEditionEnabled} = useProfilePageViewMode();

    // Computed values
    const socialNetworkIcon = computed(() => {
        switch (props.socialNetwork) {
            case 'linkedin':
                return 'linkedin';
            case 'facebook':
                return 'facebook';
            default:
                return 'social-share';
        }
    });
    const socialNetworkLink = computed(() => {
        switch (props.socialNetwork) {
            case 'linkedin':
                return linkedinSharingLink.value;
            case 'facebook':
                return facebookSharingLink.value;
            default:
                return undefined;
        }
    });
    const facebookSharingLink = computed(() => `https://www.facebook.com/sharer/sharer.php?u=${props.profileLink}`);
    const linkedinSharingLink = computed(() => `https://www.linkedin.com/shareArticle?mini=true&url=${props.profileLink}`);

    // Methods
    function handleClickOnShare() {
        trackProfileSocialShareStarted(props.socialNetwork, props.profileAccountId, isEditionEnabled.value);
        return safelyNavigateTo(socialNetworkLink.value, {externalToThisHost: true, open: {target: '_blank'}});
    }
</script>
